
import { BookOpen, Calculator, Languages, FlaskConical, Globe2, Clock, Flame, Star, Zap, Crown, Award, Coffee, Sun, Moon, Briefcase, Flag } from 'lucide-react';

// レベル定義のヘルパー
// 基準: 1(銅), 5(銀), 10(金), 20(プラチナ), 50(ダイヤ)...
// 500時間以降を大幅拡張
const TIME_THRESHOLDS = [
  { level: 1, min: 0, label: '入門', color: 'bronze' },        // 0分~
  { level: 2, min: 60, label: '初級', color: 'bronze' },       // 1時間~
  { level: 3, min: 180, label: '中級', color: 'silver' },      // 3時間~
  { level: 4, min: 300, label: '上級', color: 'silver' },      // 5時間~
  { level: 5, min: 600, label: '熟練', color: 'gold' },        // 10時間~
  { level: 6, min: 1200, label: '達人', color: 'gold' },       // 20時間~
  { level: 7, min: 3000, label: '賢者', color: 'platinum' },   // 50時間~
  { level: 8, min: 6000, label: '大賢者', color: 'platinum' }, // 100時間~
  { level: 9, min: 12000, label: '伝説', color: 'diamond' },   // 200時間~
  { level: 10, min: 30000, label: '神', color: 'diamond' },    // 500時間~
  // --- ここから拡張 ---
  { level: 11, min: 45000, label: '超越者', color: 'diamond' }, // 750時間~
  { level: 12, min: 60000, label: '求道者', color: 'diamond' }, // 1000時間~
  { level: 13, min: 90000, label: '光の巨人', color: 'diamond' }, // 1500時間~
  { level: 14, min: 120000, label: '時空の支配者', color: 'diamond' }, // 2000時間~
  { level: 15, min: 180000, label: '叡智の結晶', color: 'diamond' }, // 3000時間~
  { level: 16, min: 300000, label: 'アカシック', color: 'diamond' }, // 5000時間~
  { level: 17, min: 420000, label: '宇宙の真理', color: 'diamond' }, // 7000時間~
  { level: 18, min: 600000, label: '学問の創造主', color: 'diamond' }, // 10000時間~
];

export const EVOLVING_BADGES = {
  total_time: {
    id: 'total_time',
    name: '時の支配者',
    description: '累計学習時間の証',
    icon: Clock,
    thresholds: TIME_THRESHOLDS
  },
  streak: {
    id: 'streak',
    name: '継続の鬼',
    description: '連続学習日数の証',
    icon: Flame,
    thresholds: [
      { level: 1, min: 1, label: '三日坊主卒業', color: 'bronze' },
      { level: 2, min: 3, label: '習慣の芽', color: 'bronze' },
      { level: 3, min: 7, label: '一週間の壁', color: 'silver' },
      { level: 4, min: 14, label: '安定期', color: 'silver' },
      { level: 5, min: 30, label: '月間達成', color: 'gold' },
      { level: 6, min: 60, label: '二ヶ月継続', color: 'gold' },
      { level: 7, min: 100, label: '百日修行', color: 'platinum' },
      { level: 8, min: 365, label: '年間達成', color: 'diamond' },
      { level: 9, min: 500, label: '継続の達人', color: 'diamond' },
      { level: 10, min: 1000, label: '生ける伝説', color: 'diamond' },
    ]
  },
  // 教科別
  japanese: { id: 'japanese', name: '国語マスター', description: '国語の学習時間', icon: BookOpen, thresholds: TIME_THRESHOLDS },
  math: { id: 'math', name: '数学マスター', description: '数学の学習時間', icon: Calculator, thresholds: TIME_THRESHOLDS },
  english: { id: 'english', name: '英語マスター', description: '英語の学習時間', icon: Languages, thresholds: TIME_THRESHOLDS },
  science: { id: 'science', name: '理科マスター', description: '理科の学習時間', icon: FlaskConical, thresholds: TIME_THRESHOLDS },
  social: { id: 'social', name: '社会マスター', description: '社会の学習時間', icon: Globe2, thresholds: TIME_THRESHOLDS },
  other: { id: 'other', name: '探求者', description: 'その他の学習時間', icon: Briefcase, thresholds: TIME_THRESHOLDS },
};

// 固定バッジ（一度きりの獲得）
export const STATIC_BADGES = {
  first_record: {
    id: 'first_record',
    name: '冒険の始まり',
    description: '初めて学習を記録しました。',
    icon: Flag,
    category: 'starter'
  },
  study_30_min: {
    id: 'study_30_min',
    name: '集中モード',
    description: '1回で30分以上勉強しました。',
    icon: Zap,
    category: 'starter'
  },
  long_session: {
    id: 'long_session',
    name: 'ロングラン',
    description: '1回で2時間以上勉強しました。すごい集中力！',
    icon: Crown,
    category: 'special'
  },
  endurance_runner: {
    id: 'endurance_runner',
    name: '耐久ランナー',
    description: '1回で3時間以上勉強しました。驚異的なスタミナ！',
    icon: Flame,
    category: 'special'
  },
  iron_will: {
    id: 'iron_will',
    name: '鉄の意志',
    description: '1回で5時間以上勉強しました。もはや超人です。',
    icon: Award,
    category: 'special'
  },
  morning_glory: {
    id: 'morning_glory',
    name: '朝活マスター',
    description: '朝5:00〜9:00の間に学習を記録しました。',
    icon: Sun,
    category: 'timing'
  },
  lunch_learner: {
    id: 'lunch_learner',
    name: 'ランチ学習',
    description: 'お昼時（12:00〜13:00）に学習しました。隙間時間を有効活用！',
    icon: Coffee,
    category: 'timing'
  },
  night_owl: {
    id: 'night_owl',
    name: '夜更かし勢',
    description: '夜22:00以降に学習を記録しました。',
    icon: Moon,
    category: 'timing'
  },
  friday_night_fever: {
    id: 'friday_night_fever',
    name: '花金フィーバー',
    description: '金曜日の夜20:00以降に学習しました。遊びの誘惑に打ち勝ちました！',
    icon: Star,
    category: 'timing'
  },
  weekend_warrior: {
    id: 'weekend_warrior',
    name: '週末の戦士',
    description: '土曜日または日曜日に学習しました。',
    icon: Coffee,
    category: 'timing'
  },
};

// ヘルパー: IDからバッジ情報を取得 (進化・固定両対応)
export const getBadgeInfo = (badgeId) => {
  if (STATIC_BADGES[badgeId]) return { ...STATIC_BADGES[badgeId], type: 'static' };
  
  // 進化型バッジIDの解決 (例: 'math' -> EVOLVING_BADGES.math)
  // ただし、進化型は通常IDだけでなくレベル情報も必要なので、
  // UI側でbadgeStatsを使って描画することを推奨。
  // ここでは固定バッジの詳細取得を主目的とする。
  return null;
};
